import { format, parseISO, isToday, isYesterday, isThisWeek } from 'date-fns'
import { de } from 'date-fns/locale'

export function getInitials(name: string): string {
  return name
    .split(/[\s-]+/)
    .filter(n => n && /[A-Za-zÄÖÜäöü]/.test(n[0] ?? ''))
    .map(n => n[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function formatTime(dateString: string): string {
  const date = parseISO(dateString)

  if (isToday(date)) {
    return format(date, 'HH:mm', { locale: de })
  } else if (isYesterday(date)) {
    return 'Gestern'
  } else if (isThisWeek(date, { weekStartsOn: 1 })) {
    return format(date, 'EEE', { locale: de })
  } else {
    return format(date, 'd. MMM', { locale: de })
  }
}

export function formatDate(dateString: string | null): string {
  if (!dateString) return 'Datum offen'
  const date = parseISO(dateString)
  return format(date, 'EEE, d. MMM', { locale: de })
}

export function formatFullDate(dateString: string): string {
  const date = parseISO(dateString)
  return format(date, 'EEEE, d. MMMM yyyy', { locale: de })
}

export const CHAT_COLORS = [
  '#8b5cf6',
  '#10b981',
  '#f97316',
  '#ec4899',
  '#3b82f6',
  '#eab308',
  '#ef4444',
  '#06b6d4',
  '#84cc16',
  '#a855f7',
]

export function assignColor(index: number): string {
  return CHAT_COLORS[index % CHAT_COLORS.length] ?? CHAT_COLORS[0] ?? '#8b5cf6'
}

export const CHAT_READ_ONLY_STATUSES = new Set([
  'declined',
  'cancelled',
  'expired',
])

export function isChatReadOnlyStatus(status: string | null | undefined): boolean {
  return !!status && CHAT_READ_ONLY_STATUSES.has(status)
}

const DONE_CHAT_STATUSES = new Set([
  'accepted',
  'declined',
  'failed',
  'no_substitute_found',
  'cancelled',
  'expired',
  'booked',
])

export type ChatListFilter = 'all' | 'unread' | 'open' | 'done' | 'not_cancelled' | 'archived'

export function chatMatchesListFilter(
  chat: { request_status: string; unread_count: number; is_archived: boolean },
  filter: ChatListFilter,
): boolean {
  if (filter === 'archived') return chat.is_archived
  if (chat.is_archived) return false
  if (filter === 'unread') return chat.unread_count > 0
  if (filter === 'open') {
    return chat.request_status === 'pending' || chat.request_status === 'partially_accepted'
  }
  if (filter === 'done') return DONE_CHAT_STATUSES.has(chat.request_status)
  if (filter === 'not_cancelled') {
    return chat.request_status !== 'cancelled' || chat.unread_count > 0
  }
  return true
}

export function hashColor(userId: string): string {
  let hash = 0
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash) + userId.charCodeAt(i)
    hash |= 0
  }
  const index = Math.abs(hash) % CHAT_COLORS.length
  return CHAT_COLORS[index] ?? CHAT_COLORS[0] ?? '#8b5cf6'
}
